import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import { useDashboardData } from '../useDashboardData';
import { TileHeadline, TileHint } from '../TileFrame';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE } from '../dashboardTokens';
import { SUMMARY_VIEWS } from './summaries';
import { Dot } from './summaries/summariesShared';

// Module progress tile (#1158, specs/workshop-dashboard.md → "Module summary
// views — design guidance"): the live step's module rendered in its own
// visual idiom; between steps, the totals of every chain module whose manifest
// names a summary (#1500). A module without a view gets the generic headline +
// bars; a module without data shows its name in large type — never an empty
// tile.

const Unit = styled.span`
  font-size: ${DASH_TYPE.meta};
  font-weight: 500;
  color: ${DASH_COLORS.textDim};
  margin-left: 0.5em;
`;

const ModuleName = styled.div`
  font-size: clamp(24px, 4.5vmin, 110px);
  font-weight: 700;
  color: ${DASH_COLORS.textDim};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(4px, 0.7vmin, 16px);
  margin-top: clamp(8px, 1.2vmin, 28px);
  min-height: 0;
  overflow: hidden;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
  align-items: center;
  gap: clamp(8px, 1vmin, 24px);
`;

const RowLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5em;
  font-size: ${DASH_TYPE.meta};
  color: ${DASH_COLORS.textDim};
  overflow: hidden;
  white-space: nowrap;

  .text { overflow: hidden; text-overflow: ellipsis; }
  /* Facilitator curation is always visible: what was selected for further work */
  ${({ $selected }) => ($selected ? `color: ${DASH_COLORS.text}; font-weight: 700;` : '')}
`;

const BarTrack = styled.div`
  height: clamp(8px, 1vmin, 24px);
  border-radius: 999px;
  background: ${DASH_COLORS.track};
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  border-radius: 999px;
  background: ${DASH_COLORS.accent};
  transition: width 400ms ease;
`;

const RowValue = styled.span`
  font-family: ${DASH_FONTS.numeric};
  font-size: ${DASH_TYPE.meta};
  font-weight: 700;
`;

const TotalsRow = styled(Row)`
  grid-template-columns: minmax(0, 1fr) auto;
`;

const SectionHeading = styled.h3`
  margin: clamp(14px, 2vmin, 40px) 0 clamp(6px, 1vmin, 20px);
  font-size: ${DASH_TYPE.meta};
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${DASH_COLORS.textDim};

  &:first-of-type { margin-top: 0; }
`;

function unitLabel(t, unit) {
  return unit ? t(`dashboard.units.${unit}`) : '';
}

/** The module's human name: its chain step's label in the instance language. */
function moduleLabel(flow, moduleId, language) {
  const step = flow?.steps?.find((s) => s.moduleId === moduleId);
  return step?.labels?.[language] || step?.name || moduleId;
}

/** A row names itself, or gives a translation key for a code (a v3 pivot's kind). */
function rowLabel(t, row) {
  return row.label || (row.labelKey ? t(row.labelKey) : '—');
}

function GenericBreakdown({ t, rows, max }) {
  return (
    <Rows>
      {rows.map((row) => (
        <Row key={row.key ?? row.label ?? 'none'}>
          <RowLabel title={rowLabel(t, row)} $selected={row.selected === true}>
            {row.color && <Dot color={row.color} />}
            <span className="text">{rowLabel(t, row)}</span>
          </RowLabel>
          <BarTrack>
            <BarFill $pct={max > 0 ? Math.round((row.count / max) * 100) : 0} />
          </BarTrack>
          <RowValue>{row.count}</RowValue>
        </Row>
      ))}
    </Rows>
  );
}

function GenericSummary({ t, summary }) {
  const rows = (summary.breakdown || []).slice(0, 5);
  const max = Math.max(0, ...rows.map((r) => r.count));
  return (
    <>
      <TileHeadline>
        {summary.headline}
        <Unit>{unitLabel(t, summary.unit)}</Unit>
      </TileHeadline>
      {rows.length > 0 && <GenericBreakdown t={t} rows={rows} max={max} />}
    </>
  );
}

/** One module's summary: its idiom view, generic bars, or its bare name. */
function ModuleSummary({ t, summary, flow, language, detail = false }) {
  if (!summary.breakdown?.length) {
    return <ModuleName>{moduleLabel(flow, summary.moduleId, language)}</ModuleName>;
  }
  const view = SUMMARY_VIEWS[summary.view];
  if (view) {
    const View = detail ? view.Detail : view.Tile;
    return <View t={t} summary={summary} />;
  }
  if (detail) {
    const rows = summary.breakdown;
    const max = Math.max(0, ...rows.map((r) => r.count));
    return <GenericBreakdown t={t} rows={rows} max={max} />;
  }
  return <GenericSummary t={t} summary={summary} />;
}

export function ProgressTileBody({ t }) {
  const { language } = useTranslation();
  const { data } = useDashboardData();
  const summary = data?.moduleSummary;
  if (!summary) return <TileHint>{t('dashboard.noData')}</TileHint>;

  // Live step → its own summary, in the module's idiom. A live module with
  // no summary — a break, a social module — shows its name in large type.
  if (summary.liveModuleId) {
    const live = summary.summaries.find((s) => s.moduleId === summary.liveModuleId);
    if (!live) {
      return <ModuleName>{moduleLabel(data.flow, summary.liveModuleId, language)}</ModuleName>;
    }
    return <ModuleSummary t={t} summary={live} flow={data.flow} language={language} />;
  }

  if (!summary.summaries?.length) return <TileHint>{t('dashboard.noData')}</TileHint>;

  // Between steps → the whole workshop's totals, one line per module
  return (
    <Rows>
      {summary.summaries.map((s) => (
        <TotalsRow key={s.moduleId}>
          <RowLabel><span className="text">{moduleLabel(data.flow, s.moduleId, language)}</span></RowLabel>
          <RowValue>{s.headline} {unitLabel(t, s.unit)}</RowValue>
        </TotalsRow>
      ))}
    </Rows>
  );
}

export function ProgressTileDetail({ t }) {
  const { language } = useTranslation();
  const { data } = useDashboardData();
  const summary = data?.moduleSummary;
  if (!summary?.summaries?.length) {
    // A live module without a summary expands to its name too
    if (summary?.liveModuleId) {
      return <ModuleName>{moduleLabel(data?.flow, summary.liveModuleId, language)}</ModuleName>;
    }
    return <TileHint>{t('dashboard.noData')}</TileHint>;
  }

  return (
    <div>
      {summary.summaries.map((s) => (
        <section key={s.moduleId}>
          <SectionHeading>
            {moduleLabel(data.flow, s.moduleId, language)} — {s.headline} {unitLabel(t, s.unit)}
          </SectionHeading>
          <ModuleSummary t={t} summary={s} flow={data.flow} language={language} detail />
        </section>
      ))}
    </div>
  );
}
